import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";

/* ---------- Animated NexSys circuit-tree logo ---------- */
export function NexSysMark({ size = 30, glow = true }) {
  const nodes = [
    [16, 4], [8, 8], [24, 8], [4, 14], [12, 12], [20, 12], [28, 14],
  ];
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none" className={glow ? "nx-glow-cyan" : ""}>
      <defs>
        <linearGradient id="nxg" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#7dd3fc" />
          <stop offset="1" stopColor="#22d3ee" />
        </linearGradient>
      </defs>
      <g stroke="url(#nxg)" strokeWidth="1.4" strokeLinecap="round">
        <path d="M16 30 L16 16" />
        <path d="M16 16 L8 9 M8 9 L4 14 M8 9 L12 12" />
        <path d="M16 16 L24 9 M24 9 L28 14 M24 9 L20 12" />
        <path d="M16 16 L16 5" />
      </g>
      {nodes.map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r="1.7" fill="#67e8f9" className="nx-pulse" style={{ animationDelay: `${i * 0.22}s` }} />
      ))}
      <circle cx="16" cy="16" r="2.2" fill="#22d3ee" />
    </svg>
  );
}

export function NexSysWordmark({ size = 30, showTag = false }) {
  return (
    <div className="flex items-center gap-2.5">
      <NexSysMark size={size} />
      <div className="leading-none">
        <span className="font-display text-xl font-bold tracking-tight">
          <span className="text-white">Nex</span>
          <span className="bg-gradient-to-r from-cyan-300 to-cyan-500 bg-clip-text text-transparent">Sys</span>
        </span>
        {showTag && <div className="mt-0.5 font-mono text-[9px] tracking-[0.25em] text-cyan-400/70">COMMAND CENTER</div>}
      </div>
    </div>
  );
}

/* ---------- Panel ---------- */
export function Panel({ title, subtitle, right, corners = true, className = "", bodyClass = "", children, testid }) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      className={`nx-panel nx-panel-hover ${corners ? "nx-corners" : ""} ${className}`}
      data-testid={testid}
    >
      {(title || right) && (
        <div className="flex items-center justify-between gap-3 border-b border-white/[0.06] px-5 py-3.5">
          <div>
            {title && <h3 className="font-display text-sm font-semibold tracking-tight text-white/90">{title}</h3>}
            {subtitle && <p className="mt-0.5 font-mono text-[10px] tracking-wider text-white/35">{subtitle}</p>}
          </div>
          {right}
        </div>
      )}
      <div className={`p-5 ${bodyClass}`}>{children}</div>
    </motion.section>
  );
}

/* ---------- Status pill ---------- */
export function Pill({ tone = "cyan", children, dot = true, className = "" }) {
  const tones = {
    cyan: "text-cyan-300 border-cyan-400/30 bg-cyan-400/10",
    emerald: "text-emerald-300 border-emerald-400/30 bg-emerald-400/10",
    magenta: "text-pink-300 border-pink-400/30 bg-pink-400/10",
    amber: "text-amber-300 border-amber-400/30 bg-amber-400/10",
    indigo: "text-indigo-300 border-indigo-400/30 bg-indigo-400/10",
    danger: "text-red-300 border-red-400/30 bg-red-400/10",
  };
  const dc = { cyan: "#22d3ee", emerald: "#34d399", magenta: "#ec4899", amber: "#f59e0b", indigo: "#818cf8", danger: "#ef4444" }[tone];
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium ${tones[tone]} ${className}`}>
      {dot && <span className="h-1.5 w-1.5 rounded-full nx-pulse" style={{ background: dc }} />}
      {children}
    </span>
  );
}

/* ---------- Count up ---------- */
export function useCountUp(to, duration = 1400, decimals = 0) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(to * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [to, duration]);
  return Number(val.toFixed(decimals));
}

/* ---------- KPI tile ---------- */
export function Tile({ label, value, suffix = "", tone = "cyan", spark, trend, decimals = 0, testid, sub }) {
  const n = useCountUp(value, 1300, decimals);
  const c = { cyan: "#22d3ee", emerald: "#34d399", magenta: "#ec4899", amber: "#f59e0b", indigo: "#818cf8" }[tone];
  return (
    <div className="nx-panel nx-panel-hover nx-corners relative overflow-hidden p-4" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-white/40">{label}</span>
        {trend != null && (
          <span className="font-mono text-[10px]" style={{ color: trend >= 0 ? "#34d399" : "#ff3b6b" }}>
            {trend >= 0 ? "▲" : "▼"} {Math.abs(trend)}%
          </span>
        )}
      </div>
      <div className="mt-2 flex items-end gap-1">
        <span className="font-display text-3xl font-bold tracking-tight" style={{ color: c, textShadow: `0 0 20px ${c}55` }}>
          {n.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}
        </span>
        <span className="mb-1 text-sm text-white/50">{suffix}</span>
      </div>
      {spark && <div className="mt-3">{spark}</div>}
      {sub && <div className="mt-2 truncate font-mono text-[9px] uppercase tracking-wider text-white/30" title={sub}>{sub}</div>}
    </div>
  );
}

/* ---------- Progress ring ---------- */
export function Ring({ value, size = 190, stroke = 12, color = "#22d3ee", track = "rgba(255,255,255,0.06)", label, sub, children }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const n = useCountUp(value, 1600, 0);
  const off = c - (n / 100) * c;
  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <defs>
          <linearGradient id={`ring-${label}`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor={color} />
            <stop offset="1" stopColor="#6366f1" />
          </linearGradient>
        </defs>
        <circle cx={size / 2} cy={size / 2} r={r} stroke={track} strokeWidth={stroke} fill="none" />
        <motion.circle
          cx={size / 2} cy={size / 2} r={r} stroke={`url(#ring-${label})`} strokeWidth={stroke}
          fill="none" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={off}
          style={{ filter: `drop-shadow(0 0 8px ${color}88)` }}
        />
      </svg>
      <div className="absolute flex flex-col items-center">
        {children || (
          <>
            <span className="font-display text-4xl font-bold text-white" style={{ textShadow: `0 0 24px ${color}66` }}>{n}%</span>
            {label && <span className="mt-1 font-mono text-[10px] uppercase tracking-widest text-white/45">{label}</span>}
            {sub && <span className="mt-0.5 text-[11px] text-white/35">{sub}</span>}
          </>
        )}
      </div>
    </div>
  );
}
